import { useState, useEffect } from "react";
import AdminLayout from "@/pages/admin/components/AdminLayout";
import { supabase, NewsletterSubscriber } from "@/lib/supabase";

const AdminNewsletterPage = () => {
  const [subscribers, setSubscribers] = useState<NewsletterSubscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "active" | "unsubscribed">("all");
  const [search, setSearch] = useState("");
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const { data } = await supabase
        .from("newsletter_subscribers")
        .select("*")
        .order("created_at", { ascending: false });
      setSubscribers(data || []);
      setLoading(false);
    };
    fetchData();
  }, []);

  const handleToggleStatus = async (id: string, current: NewsletterSubscriber["status"]) => {
    const next = current === "active" ? "unsubscribed" : "active";
    setUpdatingId(id);
    await supabase.from("newsletter_subscribers").update({ status: next }).eq("id", id);
    setSubscribers((prev) => prev.map((s) => (s.id === id ? { ...s, status: next } : s)));
    setUpdatingId(null);
  };

  const handleCopyEmails = () => {
    const activeEmails = filtered
      .filter((s) => s.status === "active")
      .map((s) => s.email)
      .join(", ");
    navigator.clipboard.writeText(activeEmails);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const filtered = subscribers.filter((s) => {
    const matchStatus = filter === "all" || s.status === filter;
    const q = search.toLowerCase();
    const matchSearch = !q || s.email.toLowerCase().includes(q) || (s.source || "").toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  const activeCount = subscribers.filter((s) => s.status === "active").length;
  const unsubCount = subscribers.filter((s) => s.status === "unsubscribed").length;

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              Newsletter Subscribers
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">Manage email subscribers from the footer signup</p>
          </div>
          <button
            onClick={handleCopyEmails}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] text-white text-sm font-semibold hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
          >
            <i className={copied ? "ri-check-line" : "ri-file-copy-line"} />
            {copied ? "Copied!" : "Copy Active Emails"}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
          {[
            { label: "Total Subscribers", value: subscribers.length, icon: "ri-user-line", color: "from-[#3a8cbf] to-[#5ab0d4]" },
            { label: "Active", value: activeCount, icon: "ri-checkbox-circle-line", color: "from-green-400 to-green-500" },
            { label: "Unsubscribed", value: unsubCount, icon: "ri-user-unfollow-line", color: "from-gray-300 to-gray-400" },
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${stat.color} flex-shrink-0`}>
                <i className={`${stat.icon} text-white text-base`} />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 flex-shrink-0">
            {(["all", "active", "unsubscribed"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap capitalize ${
                  filter === f ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email..."
              className="w-full pl-9 pr-4 py-2 rounded-xl bg-gray-50 border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#3a8cbf] transition-colors"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-10 h-10 border-4 border-[#3a8cbf]/20 border-t-[#3a8cbf] rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gray-50 mx-auto mb-4">
              <i className="ri-mail-send-line text-2xl text-gray-300" />
            </div>
            <p className="text-gray-400 text-sm">No subscribers found</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">#</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Email</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider hidden sm:table-cell">Source</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider hidden md:table-cell">Subscribed On</th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((s, idx) => (
                  <tr key={s.id} className="hover:bg-gray-50/80 transition-colors">
                    <td className="px-5 py-3.5 text-sm text-gray-400">{idx + 1}</td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm font-medium text-gray-900">{s.email}</span>
                    </td>
                    <td className="px-5 py-3.5 hidden sm:table-cell">
                      <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-500 capitalize">{s.source}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span
                        className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          s.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"
                        }`}
                      >
                        {s.status === "active" ? "Active" : "Unsubscribed"}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 hidden md:table-cell">
                      <span className="text-xs text-gray-400">
                        {new Date(s.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <button
                        disabled={updatingId === s.id}
                        onClick={() => handleToggleStatus(s.id, s.status)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 ${
                          s.status === "active"
                            ? "bg-red-50 text-red-500 hover:bg-red-100"
                            : "bg-green-50 text-green-600 hover:bg-green-100"
                        }`}
                      >
                        {updatingId === s.id ? "..." : s.status === "active" ? "Unsubscribe" : "Reactivate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-5 py-3 border-t border-gray-50 flex items-center justify-between">
              <p className="text-xs text-gray-400">Showing {filtered.length} of {subscribers.length} subscribers</p>
              <p className="text-xs text-gray-400">{activeCount} active</p>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminNewsletterPage;
